import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { concatMap, debounceTime, finalize, switchMap, take } from 'rxjs/operators';

import { Cohort } from 'src/app/shared/models/cohort.model';

import { environment } from '../../../../environments/environment';
import { GoogleAnalyticsService } from '../../../core/analytics/google-analytics.service';
import { CohortService } from '../../../core/api/cohort.service';
import { GisService, ParcelNumberListDeSerialized } from '../../../core/api/gis.service';
import { IneligibleAreaUserService } from '../../../core/api/ineligible-area-user.service';
import { DialogComingSoonComponent } from '../../../shared/dialogs/dialog-coming-soon/dialog-coming-soon.component';
import { DialogIneligibleParcelComponent } from '../../../shared/dialogs/dialog-ineligible-parcel/dialog-ineligible-parcel.component';
import { Buyer, User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { clearCreateSiteStepTwo, setLocationType, setMapCenter, setSelectedRegion, } from '../../../store/parcel/parcel.actions';
import { ParcelState } from '../../../store/parcel/parcel.reducer';
import { selectParcelState } from '../../../store/parcel/parcel.selectors';

interface DropdownOption {
  value: string;
  label: string;
}

@Component({
  selector: 'fc-new-site-step-two',
  templateUrl: './new-site-step-two.component.html',
  styleUrls: ['./new-site-step-two.component.scss'],
})
export class NewSiteStepTwoComponent implements OnInit, AfterViewInit {
  destroyRef = inject(DestroyRef);

  newSiteForm: UntypedFormGroup;
  locationCoordinates: number[];

  filteredParcelNumbers: Array<ParcelNumberListDeSerialized>;

  isLoading = false;
  geocoderTouched = false;
  disableNext = true;
  email = null;
  currentUser: User;
  preferredBuyer: Buyer;

  locationTypes: DropdownOption[] = [
    { value: 'address', label: 'Address' },
    { value: 'coordinates', label: 'Lat/Long' },
    { value: 'parcelId', label: 'Parcel Number' },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly store: Store<fromApp.AppState>,
    public dialog: MatDialog,
    readonly gisService: GisService,
    readonly ineligibleAreaUserService: IneligibleAreaUserService,
    readonly cohortService: CohortService,
    readonly googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.createNewSiteForm();
  }

  ngOnInit() {
    this.store
      .select(selectParcelState)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(parcelState => {
        if (parcelState.email) {
          this.email = parcelState.email;
        } else {
          this.router.navigate(['/site/new-site']);
        }
      });

    this.newSiteForm
      .get('parcelNumber')
      .valueChanges.pipe(
        debounceTime(500),
        switchMap(value => {
          if (value.length >= 4) {
            this.geocoderTouched = true;
            this.disableNext = true;
            this.isLoading = true;
            return this.gisService.getParcelListLikeParcelNumber(value, 10).pipe(
              finalize(() => {
                this.isLoading = false;
              })
            );
          } else {
            return of([]);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(data => {
        if (data) {
          this.filteredParcelNumbers = data;
        } else {
          this.filteredParcelNumbers = [];
        }
      });

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.currentUser = user;
        this.preferredBuyer = this.currentUser?.preferredBuyer;
      });

    this.store.dispatch(clearCreateSiteStepTwo());
    window.scrollTo(0, 200);
  }

  ngAfterViewInit() {
    const forwardGeocoder = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      countries: 'us',
      placeholder: 'Enter Address',
    });

    const reverseGeocoder = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      localGeocoder: this.coordinatesGeocoder,
      placeholder: 'Enter Lat/Long',
      reverseGeocode: true,
      types: 'address',
    });

    forwardGeocoder.addTo('.forward-location-container');
    reverseGeocoder.addTo('.reverse-location-container');

    forwardGeocoder.on('result', item => {
      this.locationCoordinates = item.result.center;
      this.newSiteForm.patchValue({ addressStr: item.result.place_name });
      this.store.dispatch(
        setMapCenter({
          data: {
            lat: this.locationCoordinates[1],
            lng: this.locationCoordinates[0],
          },
        })
      );
      this.disableNext = false;
    });
    reverseGeocoder.on('result', item => {
      this.locationCoordinates = item.result.center;
      this.store.dispatch(
        setMapCenter({
          data: {
            lat: this.locationCoordinates[1],
            lng: this.locationCoordinates[0],
          },
        })
      );
      this.disableNext = false;
    });

    forwardGeocoder.on('loading', () => {
      this.disableNext = true;
    });

    reverseGeocoder.on('loading', () => {
      this.disableNext = true;
    });

    forwardGeocoder.on('results', () => {
      this.geocoderTouched = true;
    });

    reverseGeocoder.on('results', () => {
      this.geocoderTouched = true;
    });
  }

  /* given a query in the form 'lng, lat' or 'lat, lng' returns the matching
   * geographic coordinate(s) as search results in carmen geojson format,
   * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md
   */
  coordinatesGeocoder(query: string): RegExpMatchArray | any[] {
    // match anything which looks like a decimal degrees coordinate pair
    const matches = /^\s*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)\s*$/.exec(query);
    if (!matches) {
      return null;
    }

    function coordinateFeature(lng, lat) {
      return {
        center: [lng, lat],
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
        place_name: 'Lat: ' + lat + ' Lng: ' + lng,
        place_type: ['coordinate'],
        properties: {},
        type: 'Feature',
      };
    }

    const coord1 = Number(matches[1]);
    const coord2 = Number(matches[2]);
    const geocodes = [];

    if (coord1 < -90 || coord1 > 90) {
      // must be lng, lat
      geocodes.push(coordinateFeature(coord1, coord2));
    }

    if (coord2 < -90 || coord2 > 90) {
      // must be lat, lng
      geocodes.push(coordinateFeature(coord2, coord1));
    }

    if (geocodes.length === 0) {
      // else could be either lng, lat or lat, lng
      geocodes.push(coordinateFeature(coord1, coord2));
      geocodes.push(coordinateFeature(coord2, coord1));
    }

    return geocodes;
  }

  createNewSiteForm(): void {
    this.newSiteForm = this.formBuilder.group({
      locationType: '',
      parcelNumber: new UntypedFormControl(''),
      addressStr: '',
      latLngStr: '',
      lat: [null, Validators.required],
      lng: [null, Validators.required],
    });
  }

  displayFn = (item: ParcelNumberListDeSerialized): string => {
    if (item?.coordinates) {
      this.locationCoordinates = item.coordinates;
      this.store.dispatch(
        setMapCenter({
          data: {
            lat: this.locationCoordinates[1],
            lng: this.locationCoordinates[0],
          },
        })
      );
      this.disableNext = false;
      this.geocoderTouched = false;
    }
    return item?.parcelNumber ? item.parcelNumber : '';
  };

  // Form getters
  get locationType(): AbstractControl {
    return this.newSiteForm.get('locationType');
  }

  get addressStr(): string {
    return this.newSiteForm.get('addressStr').value;
  }

  get parcelNumber(): string {
    return this.newSiteForm.get('parcelNumber').value;
  }

  openComingSoonDialog(): void {
    const dialogRef = this.dialog.open(DialogComingSoonComponent, {
      panelClass: 'fc-coming-soon-dialog',
      data: { email: this.email },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      // only save user if the hiddenCheck field is untouched
      if (result.hiddenCheck === null) {
        this.store
          .select(selectParcelState)
          .pipe(
            take(1),
            concatMap(storeData => {
              const parcelState: ParcelState = storeData[0];
              const siteAcres = parcelState.acres;
              const latLng = parcelState.mapCenter.lat + ', ' + parcelState.mapCenter.lng;

              const ineligibleAreaUser = {
                email: result.email,
                address: this.addressStr,
                coordinates: latLng,
                parcel_id: this.parcelNumber,
                site_size: siteAcres,
                referrer_code: this.preferredBuyer?.referrerCode,
                state: '',
                county: '',
              };

              return this.ineligibleAreaUserService.createIneligibleAreaUser(ineligibleAreaUser);
            }),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(
            user => {
              console.log('ineligible user created', user.email);
            },
            err => {
              console.log('error creating ineligible user', err);
            }
          );
      }
    });
  }

  checkRegionAndProceed(): void {
    const lat = this.locationCoordinates[1];
    const lng = this.locationCoordinates[0];

    this.cohortService.getCohortsByLatLong(lat, lng).subscribe((cohorts: Cohort[] | null) => {
      if (cohorts && cohorts.length > 0) {
        let preferredBuyerCohorts: Cohort[] = [];
        if (this.preferredBuyer) {
          preferredBuyerCohorts = cohorts.filter(c => c.buyer.id === this.preferredBuyer.id);
        }

        if (this.preferredBuyer && preferredBuyerCohorts.length === 0) {
          // show parcel unavailable dialog
          const dialogRef = this.dialog.open(DialogIneligibleParcelComponent, {
            panelClass: 'fc-dialog-ineligible-parcel',
            data: {},
            autoFocus: false,
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result.email !== null) {
              this.store
                .select(selectParcelState)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(parcelState => {
                  const siteAcres = parcelState.acres;
                  const ineligibleAreaUser = {
                    email: result.email,
                    address: this.addressStr,
                    coordinates: lat + ', ' + lng,
                    parcel_id: this.parcelNumber,
                    site_size: siteAcres,
                    referrer_code: this.preferredBuyer?.referrerCode,
                    state: '',
                    county: '',
                  };
                  return this.ineligibleAreaUserService.createIneligibleAreaUser(ineligibleAreaUser).subscribe(
                    () => {},
                    () => console.log('ERROR creating ineligible area user'),
                    () => {}
                  );
                });
            }
          });
        } else {
          this.store.dispatch(setSelectedRegion({ data: cohorts[0].region }));
          this.store.dispatch(setLocationType({ data: this.locationType.value }));
          this.newSiteForm.markAsPristine();
          this.router.navigate(['/site/new-site/step-three']);
        }
      } else {
        this.openComingSoonDialog();
      }
    });
  }

  goBack() {
    this.router.navigate(['/site/new-site/step-one-and-a-half']);
  }

  get isFormDirty(): boolean {
    return this.newSiteForm.dirty;
  }
}
